/*
  Offer progress "nudge" at the top of the cart drawer and the cart page.

  Tells the customer how close they are to a free item for each "Buy 2 Get 1 Free" offer
  (Perfumes and Reed Diffusers), counted separately. It uses the same variant -> category map
  as the automatic free-item mechanic (#FreeGiftData, built in Liquid from the two collections)
  and it re-reads the cart from /cart.js every time instead of keeping its own tally.

  Counting: the quantity of items the customer chose in the category. Auto-added free lines
  (line item property "Free gift: Yes") are excluded, so a free item never triggers the next nudge.
*/
(function () {
  'use strict';

  var dataEl = document.getElementById('FreeGiftData');
  if (!dataEl) { return; }

  var DATA;
  try { DATA = JSON.parse(dataEl.textContent); } catch (e) { return; }

  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var CART_URL = root + 'cart.js';
  var DEBOUNCE_MS = 120;

  // Wording depends on whether the automatic free-item mechanic is installed
  var MECHANIC_INSTALLED = !!document.querySelector('script[src*="free-gift.js"]');

  var CATEGORIES = [
    {
      key: 'perfumes',
      label: 'Perfume offer',
      noun: 'perfume',
      linkText: 'Add another perfume',
      collectionUrl: root + 'collections/perfumes',
      homeSection: '.perfume-grid'
    },
    {
      key: 'diffusers',
      label: 'Diffuser offer',
      noun: 'reed diffuser',
      linkText: 'Add another reed diffuser',
      collectionUrl: root + 'collections/reed-diffusers',
      homeSection: '.grid--quick-add'
    }
  ];

  var variantCategory = {};
  var availableCount = {};
  Object.keys(DATA.categories || {}).forEach(function (key) {
    availableCount[key] = 0;
    DATA.categories[key].variants.forEach(function (v) {
      variantCategory[v.id] = key;
      if (v.available) { availableCount[key]++; }
    });
  });

  /* ------------------------------------------------------------------ the model */

  function chosenQuantities(cart) {
    var counts = { perfumes: 0, diffusers: 0 };
    cart.items.forEach(function (item) {
      var key = variantCategory[item.variant_id];
      if (!key) { return; }
      if (item.properties && item.properties['Free gift'] === 'Yes') { return; }
      counts[key] += item.quantity;
    });
    return counts;
  }

  function messageFor(cat, n) {
    if (n === 0) { return null; }

    if (n % 2 === 1) {
      // Nothing left to buy in this category: don't invite them to add one more
      if (!availableCount[cat.key]) { return null; }
      return {
        message: 'Add 1 more ' + cat.noun + ' to get ' + (n === 1 ? 'one' : 'another') + ' free',
        sub: null,
        filled: 1,
        showLink: true
      };
    }

    return {
      message: MECHANIC_INSTALLED
        ? 'Your free ' + cat.noun + ' is in your cart. Choose your fragrance below.'
        : 'Your free ' + cat.noun + ' will be applied at checkout.',
      // only suggest more when there is something left to buy
      sub: availableCount[cat.key] ? 'Add 2 more to get another free.' : null,
      filled: 2,
      showLink: false
    };
  }

  /* ----------------------------------------------------------------- the markup */

  function buildHtml(cart) {
    var counts = chosenQuantities(cart);
    var items = [];

    CATEGORIES.forEach(function (cat) {
      var m = messageFor(cat, counts[cat.key] || 0);
      if (m) { items.push({ cat: cat, m: m }); }
    });

    if (!items.length) { return ''; }

    var stacked = items.length > 1;

    return items.map(function (entry) {
      var cat = entry.cat;
      var m = entry.m;
      var html = '<div class="offer-nudge__item" data-offer-nudge-category="' + cat.key + '">';

      if (stacked) { html += '<div class="offer-nudge__label">' + cat.label + '</div>'; }

      html += '<p class="offer-nudge__message">' + m.message + '</p>';

      if (m.sub) { html += '<p class="offer-nudge__sub">' + m.sub + '</p>'; }

      html += '<div class="offer-nudge__bar" role="img" aria-label="' + m.filled + ' of 2 toward your free ' + cat.noun + '">' +
        '<span class="offer-nudge__segment' + (m.filled >= 1 ? ' is-filled' : '') + '"></span>' +
        '<span class="offer-nudge__segment' + (m.filled >= 2 ? ' is-filled' : '') + '"></span>' +
        '</div>';

      if (m.showLink) {
        html += '<a class="offer-nudge__link" href="' + cat.collectionUrl + '" data-offer-nudge-link="' + cat.key + '">' + cat.linkText + '</a>';
      }

      return html + '</div>';
    }).join('');
  }

  /* ---------------------------------------------- reading the cart and rendering */

  var requestId = 0;
  var timer = null;

  function render(html) {
    document.querySelectorAll('[data-offer-nudge]').forEach(function (el) {
      if (el.__nudgeHtml === html) { return; }
      el.innerHTML = html;
      el.__nudgeHtml = html;
      el.hidden = !html;
    });
  }

  function refresh() {
    var id = ++requestId;
    fetch(CART_URL + '?t=' + Date.now(), {
      credentials: 'same-origin',
      headers: { 'Accept': 'application/json' }
    })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (id !== requestId) { return; } // a newer read is on its way
        // render() skips any container whose content would not change, so there is no flicker
        render(buildHtml(cart));
      })
      .catch(function () { /* fail quietly: the cart still works without the nudge */ });
  }

  function schedule(delay) {
    window.clearTimeout(timer);
    timer = window.setTimeout(refresh, delay === undefined ? DEBOUNCE_MS : delay);
  }

  /* --------------------------------------------- noticing every kind of cart change */

  var CART_WRITE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;

  var wrappedFetch = window.fetch;
  if (wrappedFetch) {
    window.fetch = function (input) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      var promise = wrappedFetch.apply(this, arguments);
      if (CART_WRITE.test(url)) {
        promise.then(function () { schedule(); }, function () { /* ignore */ });
      }
      return promise;
    };
  }

  if (window.XMLHttpRequest) {
    var open = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function (method, url) {
      this.__nudgeUrl = typeof url === 'string' ? url : '';
      return open.apply(this, arguments);
    };
    var send = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.send = function () {
      if (CART_WRITE.test(this.__nudgeUrl || '')) {
        this.addEventListener('loadend', function () { schedule(); });
      }
      return send.apply(this, arguments);
    };
  }

  // the theme's own cart events, and the free-item mechanic's refresh (which is announced with cart:build)
  ['cart:updated', 'ajaxProduct:added', 'cart:build', 'drawerOpen'].forEach(function (name) {
    document.addEventListener(name, function () { schedule(); });
  });

  window.addEventListener('pageshow', function () { schedule(0); });
  document.addEventListener('visibilitychange', function () { if (!document.hidden) { schedule(200); } });

  /* ----------------------------------------------- the "Add another ..." text link */

  document.addEventListener('click', function (evt) {
    var link = evt.target && evt.target.closest ? evt.target.closest('[data-offer-nudge-link]') : null;
    if (!link) { return; }

    // only on the homepage: close the drawer and scroll to the matching product section.
    // Anywhere else the link simply goes to the collection page.
    if (!document.body.classList.contains('template-index')) { return; }

    var cat = CATEGORIES.filter(function (c) { return c.key === link.getAttribute('data-offer-nudge-link'); })[0];
    var target = cat && document.querySelector(cat.homeSection);
    var section = target && target.closest ? (target.closest('.shopify-section') || target) : null;
    if (!section) { return; }

    evt.preventDefault();
    document.dispatchEvent(new CustomEvent('cart:close'));
    window.setTimeout(function () {
      var top = section.getBoundingClientRect().top + window.pageYOffset - 90;
      window.scrollTo({ top: Math.max(top, 0), behavior: document.hidden ? 'auto' : 'smooth' });
    }, 350);
  });

  schedule(0);
})();
